import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaBars, FaTimes } from 'react-icons/fa';
import { ChevronDown, Filter, BarChart2, Users, Plug, Mail, LayoutDashboard } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isApplicationsOpen, setIsApplicationsOpen] = useState(false);
  const applicationsMenuRef = useRef<HTMLDivElement>(null);
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsApplicationsOpen(false); // Close applications dropdown when main mobile menu closes
    window.scrollTo(0, 0);
  };

  const toggleApplicationsMenu = () => {
    setIsApplicationsOpen(!isApplicationsOpen);
  };

  const applicationMenuItems = [
    {
      name: 'Funnels',
      description: 'Design and manage high-converting sales funnels.',
      icon: Filter,
      link: '/applications/funnels',
    },
    {
      name: 'Metrics',
      description: 'Track key performance indicators and analytics.',
      icon: BarChart2,
      link: '/applications/metrics',
    },
    {
      name: 'Contacts',
      description: 'Manage your customer and lead contact information.',
      icon: Users,
      link: '/applications/contacts',
    },
    {
      name: 'Integrations',
      description: 'Connect LiveFunnel with your other essential tools.',
      icon: Plug,
      link: '/applications/integrations',
    },
    {
      name: 'E-mail Lists',
      description: 'Build and manage targeted e-mail marketing lists.',
      icon: Mail,
      link: '/applications/email-lists',
    },
    {
      name: 'Dealer Consoles',
      description: 'Access specialized dashboards for dealership management.',
      icon: LayoutDashboard,
      link: '/applications/dealer-consoles',
    },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        applicationsMenuRef.current &&
        !applicationsMenuRef.current.contains(event.target as Node)
      ) {
        setIsApplicationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      <div className="container mx-auto px-4 py-5">
        <nav className="flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center text-2xl font-bold text-blue-600 flex-shrink-0"
            onClick={() => window.scrollTo(0, 0)}
          >
            <img
              src="/images/LiveFunnel.png"
              alt="LiveFunnel Logo"
              className="h-8 mr-2 object-contain"
            />
          </Link>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 text-gray-600 focus:outline-none"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
          </button>

          {/* Desktop Navigation (Consolidated) */}
          <div className="hidden md:flex flex-grow items-center">
            {' '}
            {/* Added flex-grow to push buttons to right */}
            <div className="flex items-center space-x-6 xl:space-x-8">
              {' '}
              {/* Main nav links */}
              {/* Applications Dropdown */}
              <div className="relative" ref={applicationsMenuRef}>
                <button
                  onClick={toggleApplicationsMenu}
                  className="flex items-center text-gray-700 hover:text-blue-600 font-medium focus:outline-none"
                >
                  Applications
                  <ChevronDown
                    size={18}
                    className={`ml-1 transition-transform duration-200 ${isApplicationsOpen ? 'transform rotate-180' : ''}`}
                  />
                </button>
                {isApplicationsOpen && (
                  <div className="absolute left-0 mt-2 w-80 bg-white rounded-md shadow-xl z-20 border border-gray-300 py-2">
                    {applicationMenuItems.map(item => (
                      <Link
                        key={item.name}
                        to={item.link}
                        className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 transition-colors duration-150"
                        onClick={() => {
                          setIsApplicationsOpen(false);
                          window.scrollTo(0, 0);
                        }}
                      >
                        <div className="flex items-center">
                          <item.icon size={20} className="mr-3 text-blue-600" />
                          <div>
                            <span className="font-medium text-gray-800 block">{item.name}</span>
                            <span className="text-xs text-gray-600 block">{item.description}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <Link
                to="/templates"
                className="text-gray-700 hover:text-blue-600 font-medium"
                onClick={() => window.scrollTo(0, 0)}
              >
                Templates
              </Link>
              <Link
                to="/pricing"
                className="text-gray-700 hover:text-blue-600 font-medium"
                onClick={() => window.scrollTo(0, 0)}
              >
                Pricing
              </Link>
              <Link
                to="/partners"
                className="text-gray-700 hover:text-blue-600 font-medium"
                onClick={() => window.scrollTo(0, 0)}
              >
                Partner
              </Link>
              <Link
                to="/reviews"
                className="text-gray-700 hover:text-blue-600 font-medium"
                onClick={() => window.scrollTo(0, 0)}
              >
                Reviews
              </Link>
            </div>
            <div className="ml-auto flex items-center space-x-2 md:space-x-4">
              {' '}
              {/* Buttons pushed to the right */}
              <Link
                to="/login"
                className="px-4 py-2 font-medium text-blue-600 hover:bg-blue-100 rounded-md transition-colors"
                onClick={() => window.scrollTo(0, 0)}
              >
                Login
              </Link>
              <Link
                to="/get-started"
                className="px-4 py-2 font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                onClick={() => window.scrollTo(0, 0)}
              >
                Try Now - Free
              </Link>
            </div>
          </div>
        </nav>
      </div>

      {/* Mobile Menu - Appears below header */}
      {isMenuOpen && (
        <div className="md:hidden absolute top-full right-0 bg-white shadow-lg py-4 z-40 border-t border-gray-300 w-full">
          <div className="container mx-auto px-4 flex flex-col space-y-1">
            {/* Mobile Applications Dropdown */}
            <div>
              <button
                onClick={toggleApplicationsMenu} // Re-use toggle for mobile section visibility
                className="w-full flex items-center justify-between py-2 px-2 text-gray-700 hover:text-blue-600 rounded-md hover:bg-blue-50 focus:outline-none"
              >
                <span>Applications</span>
                <ChevronDown
                  size={18}
                  className={`transition-transform duration-200 ${isApplicationsOpen ? 'transform rotate-180' : ''}`}
                />
              </button>
              {isApplicationsOpen && (
                <div className="pl-4 mt-1 space-y-1 border-l-2 border-gray-300 ml-2">
                  {applicationMenuItems.map(item => (
                    <Link
                      key={item.name}
                      to={item.link}
                      className="block py-2 px-2 text-sm text-gray-700 hover:text-blue-600 rounded-md hover:bg-blue-50 transition-colors duration-150"
                      onClick={closeMenu} // This will also close the applications dropdown
                    >
                      <div className="flex items-center">
                        <item.icon size={18} className="mr-2 text-blue-600 flex-shrink-0" />
                        <div>
                          <span className="font-medium text-gray-800 block">{item.name}</span>
                          <span className="text-xs text-gray-600 block leading-tight">
                            {item.description}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <Link
              to="/templates"
              className="block py-2 px-2 text-gray-700 hover:text-blue-600 rounded-md hover:bg-blue-50"
              onClick={closeMenu}
            >
              Templates
            </Link>
            <Link
              to="/pricing"
              className="block py-2 px-2 text-gray-700 hover:text-blue-600 rounded-md hover:bg-blue-50"
              onClick={closeMenu}
            >
              Pricing
            </Link>
            <Link
              to="/partners"
              className="block py-2 px-2 text-gray-700 hover:text-blue-600 rounded-md hover:bg-blue-50"
              onClick={closeMenu}
            >
              Partner
            </Link>
            <Link
              to="/reviews"
              className="block py-2 px-2 text-gray-700 hover:text-blue-600 rounded-md hover:bg-blue-50"
              onClick={closeMenu}
            >
              Reviews
            </Link>

            <div className="border-t border-gray-200 pt-3 mt-3 flex flex-col space-y-3">
              <Link
                to="/login"
                className="block py-2 px-2 text-blue-600 hover:bg-blue-100 rounded-md text-center font-medium"
                onClick={closeMenu}
              >
                Login
              </Link>
              <Link
                to="/get-started"
                className="block py-2 px-2 text-white bg-blue-600 hover:bg-blue-700 rounded-md text-center font-semibold"
                onClick={closeMenu}
              >
                Try Now - Free
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
